import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUpload = ({ value, onChange, label = "Upload de Imagem" }) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const compressImage = (file, maxWidth = 1920, quality = 0.85) => {
    return new Promise((resolve) => {
      if (file.size < 1024 * 1024) {
        resolve(file);
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' })),
            'image/jpeg',
            quality
          );
        };
        img.onerror = () => resolve(file);
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  const handleUpload = async (file) => {
    if (!file) return;

    // Validar tipo
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      alert('Tipo de arquivo não permitido. Use JPEG, PNG, WEBP ou GIF.');
      return;
    }

    // Validar tamanho (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Arquivo muito grande. Tamanho máximo: 10MB');
      return;
    }

    setUploading(true);

    try {
      const compressed = await compressImage(file);
      const formData = new FormData();
      formData.append('file', compressed);

      const token = localStorage.getItem('admin_token');
      console.log('Starting image upload...');
      
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Upload response:', response.data);

      if (response.data && response.data.success) {
        // Usar rota da API para servir imagens (workaround para produção)
        const filename = response.data.filename;
        const imageUrl = `${BACKEND_URL}/api/admin/image/${filename}`;
        console.log('Image uploaded successfully! API URL:', imageUrl);
        onChange(imageUrl);
      } else {
        console.error('Upload failed - no success in response:', response.data);
        alert('Erro ao fazer upload da imagem. Resposta inválida do servidor.');
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      console.error('Error details:', error.response?.data);
      alert(`Erro ao fazer upload da imagem: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleUpload(e.target.files[0]);
    }
  };

  const handleButtonClick = () => {
    inputRef.current?.click();
  };

  const handleRemove = () => {
    onChange('');
  };

  return (
    <div className="space-y-2" data-testid="image-upload-container">
      <label className="text-sm font-medium">{label}</label>
      
      {value ? (
        <div className="relative" data-testid="image-upload-preview">
          <img 
            src={value} 
            alt="Preview" 
            className="w-full h-48 object-cover rounded-lg border border-slate-300"
            data-testid="image-upload-preview-img"
          />
          <div className="absolute top-2 right-2 flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="destructive"
              onClick={handleRemove}
              data-testid="image-upload-remove-btn"
            >
              <X className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleButtonClick}
              data-testid="image-upload-change-btn"
            >
              Trocar
            </Button>
          </div>
        </div>
      ) : (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragActive 
              ? 'border-brand-medium-light500 bg-brand-light' 
              : 'border-slate-300 hover:border-brand-medium-light400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleButtonClick}
          data-testid="image-upload-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
            onChange={handleChange}
            data-testid="image-upload-input"
          />
          
          {uploading ? (
            <div className="space-y-2">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-medium-light500 mx-auto"></div>
              <p className="text-sm text-slate-600">Fazendo upload...</p>
            </div>
          ) : (
            <div className="space-y-2">
              <ImageIcon className="w-12 h-12 text-slate-400 mx-auto" />
              <div>
                <p className="text-sm text-slate-600">
                  Arraste uma imagem ou <span className="text-brand-dark font-medium">clique para selecionar</span>
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  JPEG, PNG, WEBP ou GIF (máx. 10MB)
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
